// The maintenance cover (the release manager, docs/design/deployment.md
// §6.1; ui-map-interface.md §8).
//
// A maintenance window stops the game instances. A page that was already
// open is then looking at a scene nothing moves any more, so the client
// puts a full-screen cover over it that says what is happening and when the
// game comes back. The cover reloads the page by itself the moment the gate
// comes down, so a person who walked away comes back to the game and not to
// a dead tab.
//
// **The cover takes no press.** It carries a title, the operator's line, a
// countdown and the Sonar Bloom. There is nothing to decide and therefore
// nothing to control: every control on it would be a control that does not
// work, because nothing behind the gate answers.
//
// **One bloom, loaded on demand.** `client/pages/bloom.js` is the one
// source of the arrival's wavefront, and `balancegen` copies it to
// `client/public/bloom.js` for the static maintenance page. The cover loads
// that same file with a script element and calls `window.SonarBloom.start`,
// so the bundle carries no second copy of it and a page that never shows
// the cover never pays for it.
//
// **The clock is the server's** (the R220 rule). The countdown is anchored
// on the `Date` header of the gate document, not on the device clock, so a
// machine with a wrong clock still counts down to the right instant.
//
// It mounts on `document.body` and not inside `#ui`: `#ui` is a stacking
// context of its own, so a cover inside it could not stand over `#fatal`
// (R182), and the hub and the landing screen have no `#ui` at all.

import { MAINTENANCE_POLL_SEC } from '../gen/balance';
import { maintenance as S } from '../gen/strings';
import { isoNear } from '../locale/stamp';
import {
  endPhase, type MaintenanceInfo, nextPollMs, probe, reloadDelayMs,
} from '../net/maintenance';
import { hmmss } from '../locale/clock';

/** What `bloom.js` publishes once it has loaded. */
declare global {
  interface Window {
    SonarBloom?: { start(canvas: HTMLCanvasElement): void; stop(): void };
  }
}

/** The cover's element id, so a second call finds the first cover. */
const COVER_ID = 'maintenance';

/** The one cover. A second `showMaintenanceCover` updates it. */
let cover: Cover | null = null;

/** The parts of the cover a poll rewrites, and the state it counts from. */
interface Cover {
  root: HTMLElement;
  note: HTMLElement;
  left: HTMLElement;
  /** The countdown, a bare span on a line of its own. */
  count: HTMLElement;
  /** The unix second the window is planned to close, 0 when none is known. */
  until: number;
  /** Server second minus device second, so the countdown reads the server. */
  skewSec: number;
  /** The poll timer, so a second call does not start a second loop. */
  timer: number;
  /** The one-second timer the countdown line is rewritten on. */
  clock: number;
  /** Set once the gate is gone: the page is on its way out and the loop
   *  must stop writing to a document that is being replaced. */
  done: boolean;
  /** The end this cover already asked about at zero, so the ask at zero
   *  happens once per end (R1191). */
  zeroAsked: number;
}

/**
 * Puts the cover on the screen, or updates the one that is already there.
 *
 * `info` is what `probe()` answered, or null where the client knows a
 * window stands but could not read the document — a host that answers
 * nothing at all. The cover then carries the title and the note and counts
 * down to nothing, which is the honest picture: the game is down and
 * nobody can say for how long.
 */
function showMaintenanceCover(info: MaintenanceInfo | null): void {
  if (cover) {
    applyInfo(cover, info);
    return;
  }
  cover = build();
  applyInfo(cover, info);
  startBloom(cover);
  schedule(cover);
  // One rewrite a second, and it starts HERE and not at import time: a
  // page that never shows the cover must not carry a timer for it.
  cover.clock = window.setInterval(() => { if (cover) tick(cover); }, 1000);
  document.addEventListener('visibilitychange', () => {
    if (!cover || cover.done) return;
    if (document.hidden) {
      window.clearTimeout(cover.timer);
      return;
    }
    // The window may have ended while nobody looked, so the first ask
    // after the return is immediate.
    void poll(cover);
    schedule(cover);
  });
}

/** True while the cover stands. The caller uses it to stop covering twice. */
function maintenanceCoverIsUp(): boolean {
  return cover !== null;
}

/** How many times the client asks the gate document before it covers the
 *  screen with no countdown. Close code 4004 is proof that a window
 *  stands; a host that is already down answers nothing, so the client asks
 *  again on the poll cadence rather than deciding on one silence. */
const PROBE_TRIES = 3;

/**
 * Asks the gate document and covers the screen where a window stands. Both
 * pages that open a world socket call it — the game and the arcade — so the
 * rule is written once.
 *
 * `certain` says whether the client already KNOWS a window stands: close
 * code 4004 is that proof. A mere suspicion — three failed reconnects —
 * covers nothing unless the document answers, because the usual cause of a
 * failed reconnect is a network that came back.
 *
 * A host that is already down answers nothing, and that silence is not
 * proof the window is over: the world may have closed this socket a moment
 * before nginx came up behind the gate. So a certain caller asks again on
 * the poll cadence, three times over, and the cover then goes up with no
 * countdown, which is the honest picture.
 *
 * `before` runs once, just before the cover goes up, for a caller that has
 * something of its own to take off the screen first.
 */
export async function coverIfMaintenance(
  certain: boolean, before?: () => void, tries = 0,
): Promise<void> {
  if (maintenanceCoverIsUp()) return;
  const info = await probe();
  if (info || (certain && tries + 1 >= PROBE_TRIES)) {
    before?.();
    showMaintenanceCover(info);
    return;
  }
  if (!certain) return;
  window.setTimeout(() => {
    void coverIfMaintenance(certain, before, tries + 1);
  }, MAINTENANCE_POLL_SEC * 1000);
}

/** Builds the cover's DOM. Text goes in as TEXT and never as markup: the
 *  note is an operator's line and the cover must not render it (§15). */
function build(): Cover {
  const root = document.createElement('div');
  root.id = COVER_ID;

  const canvas = document.createElement('canvas');
  canvas.id = 'maintenance-bloom';
  root.appendChild(canvas);

  const div = (cls: string, host: HTMLElement, text = ''): HTMLElement => {
    const el = document.createElement('div');
    el.className = cls;
    el.textContent = text;
    return host.appendChild(el);
  };
  const plate = div('plate', root);
  div('title', plate, S.title);
  const note = div('note', plate);
  const left = div('left', plate);
  const count = div('count', plate);

  document.body.appendChild(root);
  return {
    root, note, left, count, until: 0, skewSec: 0, timer: 0, clock: 0, done: false, zeroAsked: 0,
  };
}

/** Takes one probe answer and rewrites the lines: the note, the end of the
 *  window, and the skew that makes the countdown read the server's clock
 *  and not the device's. A null answer keeps the end and the skew. */
function applyInfo(c: Cover, info: MaintenanceInfo | null): void {
  c.note.textContent = info && info.note !== '' ? info.note : S.note_default;
  if (info) {
    c.until = info.until;
    c.skewSec = info.now - Math.floor(Date.now() / 1000);
  }
  tick(c);
}

/** The server's own second. */
function serverNow(c: Cover): number {
  return Math.floor(Date.now() / 1000) + c.skewSec;
}

/** Writes the countdown line for this second. */
function tick(c: Cover): void {
  if (c.done) return;
  if (c.until <= 0) {
    c.left.textContent = '';
    c.count.textContent = '';
    return;
  }
  const leftSec = c.until - serverNow(c);
  if (leftSec <= 0 && c.zeroAsked !== c.until) {
    // Zero: the gate comes down on this second (R1188), so the cover asks
    // at once and then every second of the burst (R1191).
    c.zeroAsked = c.until;
    void poll(c);
    schedule(c);
  }
  if (endPhase(leftSec) === 'long') {
    // The window ran past the instant it planned and the grace. The cover
    // keeps polling, so the reader has nothing to do.
    c.left.textContent = S.line_running_long;
    c.count.textContent = '';
    return;
  }
  // The instant in one sentence, and the span under it as a bare figure: a
  // person who comes back to a tab reads the instant, and a person who waits
  // reads the span. Inside the grace the span reads 0:00.
  c.left.textContent = S.line_back_at({ time: isoNear(c.until, serverNow(c)) });
  // M:SS, or H:MM:SS past an hour: the count the maintenance page shows,
  // so both covers tick on the same face.
  c.count.textContent = hmmss(Math.max(0, leftSec));
}

/** Asks the gate document once. A null answer is the gate coming down. */
async function poll(c: Cover): Promise<void> {
  if (c.done) return;
  const info = await probe();
  if (c.done) return;
  if (!info) {
    c.done = true;
    window.clearTimeout(c.timer);
    window.clearInterval(c.clock);
    c.left.textContent = S.line_reloading;
    c.count.textContent = '';
    // A random pause, so the players who waited do not all join in one
    // millisecond (R1191).
    window.setTimeout(() => location.reload(), reloadDelayMs());
    return;
  }
  applyInfo(c, info);
}

/**
 * Puts the poll on its cadence, and the countdown on one second.
 *
 * A cover nobody looks at polls nothing: a tab left open for a day must not
 * ask the origin thousands of times. The countdown itself keeps ticking,
 * because a hidden tab's timer is throttled by the browser anyway and the
 * line is redrawn from the anchor rather than counted down step by step.
 * From zero to the end of the burst the cadence is one second (R1191).
 */
function schedule(c: Cover): void {
  window.clearTimeout(c.timer);
  if (c.done || document.hidden) return;
  const left = c.until > 0 ? c.until - serverNow(c) : null;
  c.timer = window.setTimeout(() => {
    void poll(c);
    schedule(c);
  }, nextPollMs(left));
}

/** Loads `/bloom.js` once and starts the wavefront on the cover's canvas. */
function startBloom(c: Cover): void {
  const canvas = c.root.querySelector<HTMLCanvasElement>('canvas');
  if (!canvas) return;
  const start = () => window.SonarBloom?.start(canvas);
  if (window.SonarBloom) {
    start();
    return;
  }
  const script = document.createElement('script');
  script.src = '/bloom.js';
  script.defer = true;
  script.addEventListener('load', start);
  // A bloom that does not load costs the cover nothing: the plate carries
  // every word, and the canvas stays an empty dark rectangle.
  document.head.appendChild(script);
}
